import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from 'src/app/configs/app.config';
import { AutoCompleteItem } from './interfaces/autocomplete-item';

/** Delay so SR finishes the keystroke before announcing loading (WCAG 4.1.3; matches preloader pattern). */
const COMBOBOX_LOADING_A11Y_DELAY_MS = 120;

@Component({
  selector: 'app-auto-complete',
  templateUrl: './auto-complete.component.html'
})
export class AutoCompleteComponent {
  items: AutoCompleteItem[] = [];
  @Input() set itemsData(items: AutoCompleteItem[]) {
    this.clearLoadingAnnounceTimeout();
    this.isLoading = false;

    if (!items) {
      return;
    }

    this.filterItemsByMultiple(items);
  }

  @Input() multiple = false;
  @Input() disabled = false;
  @Input() selectedItem: AutoCompleteItem | AutoCompleteItem[] | null | undefined;
  @Input() minTermLength: number = AppConfig.AutoComplete.minTermLength;
  @Input() searchOnClick = false;
  /** ID for the inner combobox input (for label association, WCAG 3.3.2). Pass with ariaLabel. */
  @Input() inputId: string | undefined;
  /** Accessible name for the combobox (e.g. translated label). Pass with inputId. */
  @Input() ariaLabel: string | undefined;
  /** Extra attributes for the inner input. Merged with autocomplete: 'off'. */
  @Input() inputAttrs: Record<string, string> = {};
  /** WCAG 3.3.1 — id of validation message element (must match app-form-error errorElementId). */
  @Input() describedBy: string | null | undefined;
  /** WCAG 3.3.1 — invalid state on the combobox search input */
  @Input() ariaInvalid: boolean | undefined;
  /** WCAG 2.5.3 — id of the visible &lt;label&gt; for aria-labelledby (e.g. &lt;label id="…-a11y-label"&gt;). */
  @Input() labelElementId: string | undefined;

  @Output() selectChange = new EventEmitter<AutoCompleteItem[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() selectedItemChange = new EventEmitter<AutoCompleteItem>();
  @Output() removeItemChange = new EventEmitter<AutoCompleteItem[]>();

  constructor(
    private liveAnnouncer: LiveAnnouncer,
    protected translate: TranslateService
  ) {}

  get effectiveInputId(): string {
    return this.inputId ?? 'auto-complete-select';
  }

  get effectiveInputAttrs(): Record<string, string> {
    return { autocomplete: 'off', ...this.inputAttrs };
  }

  isLoading = false;
  debounce: any;
  timeout: any;
  lastSentTerm: string | undefined;
  private loadingAnnounceTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private clearLoadingAnnounceTimeout(): void {
    if (this.loadingAnnounceTimeoutId != null) {
      clearTimeout(this.loadingAnnounceTimeoutId);
      this.loadingAnnounceTimeoutId = null;
    }
  }

  onChange(item: AutoCompleteItem): void {
    this.emitSelectChange([item]);
    this.emitSelectItemChange(item);
  }

  onSearch(item: { term: string; items: any[] }): void {
    clearTimeout(this.debounce);
    clearTimeout(this.timeout);
    this.clearLoadingAnnounceTimeout();

    if (item.term.length < this.minTermLength) {
      this.clearItems();
      return;
    }

    // Triggers select event after debounceTimeMs
    // Protects API from requesting a new data, every time when the user types a new symbol
    this.debounce = setTimeout(() => {
      // Prevents emitting of the event if the last send value is the same as current one
      // Example: If you type something and quickly deleted until debounceTimeMs.
      // This action wont be emitted because assigning of new value happend inside debounce.
      // This prevents API from additional requests that already is loaded
      if (this.lastSentTerm === item.term) {
        return;
      }

      this.isLoading = true;
      this.lastSentTerm = item.term;
      this.clearItems();

      this.loadingAnnounceTimeoutId = setTimeout(() => {
        this.loadingAnnounceTimeoutId = null;
        void this.liveAnnouncer.announce(this.translate.instant('A11Y_COMBOBOX_LOADING'), 'polite');
      }, COMBOBOX_LOADING_A11Y_DELAY_MS);

      this.searchChange.emit(item.term);
    }, AppConfig.AutoComplete.debounceTimeMs);

    // Hides loading after defined clearTimeOut
    // Protects UI from showing infinity loading state, if some error occurred
    this.timeout = setTimeout(() => {
      this.isLoading = false;
      this.clearLoadingAnnounceTimeout();
    }, AppConfig.AutoComplete.clearTimeOut);
  }

  onOpen(): void {
    this.clearItems();

    if (this.searchOnClick) {
      this.searchChange.emit();
    }
  }

  compareWith(item: AutoCompleteItem, selected: AutoCompleteItem): boolean {
    return item?.label?.toLowerCase() === selected?.label?.toLowerCase();
  }

  emitSelectChange(selectedItems: AutoCompleteItem[]): void {
    this.selectChange.emit(selectedItems);
  }

  emitSelectItemChange(selectedItem: AutoCompleteItem): void {
    this.selectedItemChange.emit(selectedItem);
  }

  clearItems(): void {
    if (!Array.isArray(this.selectedItem)) {
      this.items = [];
    }
  }

  onRemoveItem(selectedItem: AutoCompleteItem): void {
    if (Array.isArray(this.selectedItem)) {
      this.selectedItem = this.selectedItem?.filter(item => item.value !== selectedItem.value);
      this.removeItemChange.emit(this.selectedItem);
    }
  }

  /**
   * Filters dublicate items if we use multiple option, otherwise we just update this.items
   * Note: if this.selectedItem is array actually this means that multiple option is turned on.
   */
  private filterItemsByMultiple(items: AutoCompleteItem[]): void {
    if (!Array.isArray(this.selectedItem)) {
      this.items = [...items];
      return;
    }

    const selectedItems: AutoCompleteItem[] = [...this.selectedItem];

    this.items = [...items].filter(newItem => {
      return selectedItems.find(selectedItem => selectedItem.value === newItem.value) === undefined;
    });
  }
}
